import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import SafeImage from "@/components/SafeImage";
import { toast } from "sonner";
import { Upload, X, GripVertical } from "lucide-react";

const empty = {
  title: "", short_description: "", full_description: "", price: 0,
  compare_at_price: null, sale_price: null, category: "", collection: "", collections: [],
  featured: false, is_new_arrival: false, is_sale: false,
  inventory_quantity: 1, inventory_type: "standard", product_status: "active",
  sold_archive_visible: true, images: [], primary_image: null, image_alt: "",
  media_type: "2d", spin_360_images: [], model_3d_url: null, model_3d_poster: null,
  materials: "", condition: "",
  sku: "", brand: "", gtin: "", mpn: "",
  seo_title: "", seo_description: "", og_image: null,
};

export default function ProductEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(empty);
  const [collections, setCollections] = useState([]);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(); const spinRef = useRef(); const modelRef = useRef();

  useEffect(() => {
    api.get("/admin/collections").then((r) => setCollections(r.data));
    if (id && id !== "new") {
      api.get("/admin/products").then((r) => {
        const found = r.data.find((x) => x.id === id);
        if (found) setP({ ...empty, ...found });
      });
    }
  }, [id]);

  const update = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const uploadFiles = async (files, target) => {
    const urls = [];
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const r = await api.post("/admin/upload?kind=image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(r.data.url);
      } catch { toast.error("Upload failed"); }
    }
    if (target === "images") setP((s) => ({ ...s, images: [...s.images, ...urls], primary_image: s.primary_image || urls[0] }));
    if (target === "spin") setP((s) => ({ ...s, spin_360_images: [...s.spin_360_images, ...urls] }));
  };

  const uploadModel = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    // For GLB, backend expects image kind restriction — send with kind=model bypass by using image endpoint; actually storage accepts any. We'll skip validation on backend by using ?kind=other
    try {
      const r = await api.post("/admin/upload?kind=other", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("model_3d_url", r.data.url);
      toast.success("3D model uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const removeImg = (idx, target) => setP((s) => {
    const arr = [...s[target]]; arr.splice(idx, 1);
    const next = { ...s, [target]: arr };
    if (target === "images" && s.primary_image === s.images[idx]) next.primary_image = arr[0] || null;
    return next;
  });

  const moveImg = (idx, dir, target) => setP((s) => {
    const arr = [...s[target]]; const j = idx + dir; if (j < 0 || j >= arr.length) return s;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; return { ...s, [target]: arr };
  });

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...p, price: parseFloat(p.price) || 0, inventory_quantity: parseInt(p.inventory_quantity) || 0,
                     compare_at_price: p.compare_at_price ? parseFloat(p.compare_at_price) : null,
                     sale_price: p.sale_price ? parseFloat(p.sale_price) : null };
      if (id && id !== "new") await api.put(`/admin/products/${id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Saved"); nav("/admin/products");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="font-display text-3xl text-gold mb-8">{id === "new" ? "New Product" : "Edit Product"}</h1>
      <div className="space-y-6">
        <Field label="Title" testid="pe-title"><input value={p.title} onChange={(e) => update("title", e.target.value)} className="input-vintage" data-testid="pe-title" /></Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Price ($)"><input type="number" step="0.01" value={p.price} onChange={(e) => update("price", e.target.value)} className="input-vintage" data-testid="pe-price" /></Field>
          <Field label="Sale Price (optional)"><input type="number" step="0.01" value={p.sale_price || ""} onChange={(e) => update("sale_price", e.target.value)} className="input-vintage" data-testid="pe-sale-price" /></Field>
        </div>
        <Field label="Compare-at Price (original before sale)"><input type="number" step="0.01" value={p.compare_at_price || ""} onChange={(e) => update("compare_at_price", e.target.value)} className="input-vintage" /></Field>

        <Field label="Short Description"><input value={p.short_description} onChange={(e) => update("short_description", e.target.value)} className="input-vintage" data-testid="pe-short-desc" /></Field>
        <Field label="Full Description"><textarea rows={4} value={p.full_description} onChange={(e) => update("full_description", e.target.value)} className="input-vintage" /></Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Category (free text)"><input value={p.category} onChange={(e) => update("category", e.target.value)} className="input-vintage" /></Field>
          <Field label="Primary Collection">
            <select value={p.collection} onChange={(e) => update("collection", e.target.value)} className="input-vintage" data-testid="pe-collection">
              <option value="">— none —</option>
              {collections.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
            </select>
          </Field>
        </div>

        <div>
          <label className="font-caption text-[10px] text-brass block mb-1">Additional Collections</label>
          <div className="flex flex-wrap gap-2" data-testid="pe-collections-multi">
            {collections.map((c) => {
              const on = (p.collections || []).includes(c.slug);
              return (
                <button type="button" key={c.id} data-testid={`pe-col-${c.slug}`}
                  onClick={() => setP((s) => ({ ...s, collections: on ? (s.collections || []).filter((x) => x !== c.slug) : [ ...(s.collections || []), c.slug ] }))}
                  className={`text-[11px] font-caption px-3 py-1 border ${on ? "border-gold text-gold bg-[rgba(212,175,55,0.06)]" : "border-brass-soft text-parchment/70 hover:text-gold"}`}>
                  {c.name}
                </button>
              );
            })}
            {collections.length === 0 && <span className="text-parchment/50 text-xs italic">No collections yet — create one in Collections.</span>}
          </div>
          <p className="text-[11px] text-parchment/50 mt-2">A product can belong to multiple collections/categories.</p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Field label="Inventory Type">
            <select value={p.inventory_type} onChange={(e) => update("inventory_type", e.target.value)} className="input-vintage" data-testid="pe-inv-type">
              <option value="standard">Standard</option><option value="limited">Limited</option><option value="one_of_one">One of One</option>
            </select>
          </Field>
          <Field label="Quantity"><input type="number" value={p.inventory_quantity} onChange={(e) => update("inventory_quantity", e.target.value)} className="input-vintage" /></Field>
          <Field label="Status">
            <select value={p.product_status} onChange={(e) => update("product_status", e.target.value)} className="input-vintage">
              <option value="draft">Draft</option><option value="active">Active</option><option value="sold_out">Sold Out</option><option value="archived">Archived</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Materials"><input value={p.materials} onChange={(e) => update("materials", e.target.value)} className="input-vintage" /></Field>
          <Field label="Condition"><input value={p.condition} onChange={(e) => update("condition", e.target.value)} className="input-vintage" /></Field>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-3">
          <label className="flex items-center gap-2 text-sm text-parchment/80"><input type="checkbox" checked={p.featured} onChange={(e) => update("featured", e.target.checked)} data-testid="pe-featured" /> Featured</label>
          <label className="flex items-center gap-2 text-sm text-parchment/80"><input type="checkbox" checked={p.is_new_arrival} onChange={(e) => update("is_new_arrival", e.target.checked)} data-testid="pe-new-arrival" /> New Arrival</label>
          <label className="flex items-center gap-2 text-sm text-parchment/80"><input type="checkbox" checked={p.is_sale} onChange={(e) => update("is_sale", e.target.checked)} data-testid="pe-sale" /> On Sale</label>
          <label className="flex items-center gap-2 text-sm text-parchment/80"><input type="checkbox" checked={p.sold_archive_visible} onChange={(e) => update("sold_archive_visible", e.target.checked)} /> Show in archive if sold</label>
        </div>

        <Field label="Media Type">
          <select value={p.media_type} onChange={(e) => update("media_type", e.target.value)} className="input-vintage" data-testid="pe-media-type">
            <option value="2d">2D Photos</option><option value="360">360° Spin</option><option value="3d">3D Model</option><option value="mixed">Mixed Media</option>
          </select>
        </Field>

        {/* Images */}
        <div>
          <div className="font-caption text-xs text-brass mb-2">Photographs</div>
          <div className="grid grid-cols-4 gap-3">
            {p.images.map((img, i) => (
              <div key={i} className="relative group border border-brass-soft aspect-square">
                <SafeImage src={img} alt="" className="w-full h-full object-cover cursor-pointer" onClick={() => update("primary_image", img)} />
                {p.primary_image === img && <div className="absolute top-1 left-1 bg-gold text-forest text-[9px] font-caption px-1">PRIMARY</div>}
                <button onClick={() => removeImg(i, "images")} className="absolute top-1 right-1 bg-forest/80 p-1 text-oxblood"><X size={12} /></button>
                <div className="absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100">
                  <button onClick={() => moveImg(i, -1, "images")} className="bg-forest/80 text-gold px-1 text-xs">‹</button>
                  <button onClick={() => moveImg(i, 1, "images")} className="bg-forest/80 text-gold px-1 text-xs">›</button>
                </div>
              </div>
            ))}
            <button onClick={() => imgRef.current?.click()} data-testid="upload-images-btn" className="aspect-square border-2 border-dashed border-brass/40 flex flex-col items-center justify-center text-brass hover:border-gold hover:text-gold">
              <Upload size={20} /><span className="text-[10px] font-caption mt-1">Upload</span>
            </button>
            <input ref={imgRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadFiles([...e.target.files], "images")} />
          </div>
          <p className="text-[11px] text-parchment/50 mt-2">Click a photo to set primary.</p>
        </div>

        {/* 360 sequence */}
        {(p.media_type === "360" || p.media_type === "mixed") && (
          <div>
            <div className="font-caption text-xs text-brass mb-2">360° Sequence ({p.spin_360_images.length} frames)</div>
            <div className="grid grid-cols-8 gap-2">
              {p.spin_360_images.map((img, i) => (
                <div key={i} className="relative aspect-square border border-brass-soft">
                  <SafeImage src={img} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImg(i, "spin_360_images")} className="absolute top-0 right-0 bg-forest/80 p-0.5 text-oxblood"><X size={10} /></button>
                </div>
              ))}
              <button onClick={() => spinRef.current?.click()} className="aspect-square border-2 border-dashed border-brass/40 flex items-center justify-center text-brass hover:text-gold"><Upload size={16} /></button>
              <input ref={spinRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadFiles([...e.target.files], "spin")} />
            </div>
          </div>
        )}

        {/* 3D model */}
        {(p.media_type === "3d" || p.media_type === "mixed") && (
          <div>
            <div className="font-caption text-xs text-brass mb-2">3D Model (GLB/GLTF)</div>
            {p.model_3d_url && <div className="text-parchment/70 text-sm mb-2">✓ {p.model_3d_url.split("/").pop()}</div>}
            <button onClick={() => modelRef.current?.click()} className="btn-outline text-xs"><Upload size={14} className="mr-2" /> Upload GLB</button>
            <input ref={modelRef} type="file" accept=".glb,.gltf,model/gltf-binary" hidden onChange={(e) => e.target.files[0] && uploadModel(e.target.files[0])} />
          </div>
        )}

        <Field label="Primary Image Alt Text (accessibility &amp; SEO)">
          <input value={p.image_alt || ""} onChange={(e) => update("image_alt", e.target.value)} className="input-vintage" data-testid="pe-image-alt" placeholder="e.g. Brass crescent locket on cream linen" />
        </Field>

        <details className="border-t border-brass-soft pt-4">
          <summary className="font-caption text-xs text-brass cursor-pointer">SEO &amp; Social Sharing</summary>
          <div className="space-y-3 mt-3">
            <Field label="SEO Title (falls back to product title)"><input value={p.seo_title || ""} onChange={(e) => update("seo_title", e.target.value)} className="input-vintage" data-testid="pe-seo-title" /></Field>
            <Field label="Meta Description (falls back to short description)"><textarea rows={2} value={p.seo_description || ""} onChange={(e) => update("seo_description", e.target.value)} className="input-vintage" /></Field>
            <Field label="Social Share Image URL (falls back to primary image)"><input value={p.og_image || ""} onChange={(e) => update("og_image", e.target.value)} className="input-vintage" /></Field>
          </div>
        </details>

        <details className="border-t border-brass-soft pt-4">
          <summary className="font-caption text-xs text-brass cursor-pointer">Product Identifiers (Google Merchant ready)</summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <Field label="SKU"><input value={p.sku || ""} onChange={(e) => update("sku", e.target.value)} className="input-vintage" data-testid="pe-sku" /></Field>
            <Field label="Brand"><input value={p.brand || ""} onChange={(e) => update("brand", e.target.value)} className="input-vintage" data-testid="pe-brand" placeholder="Golden Moth Market" /></Field>
            <Field label="GTIN (optional)"><input value={p.gtin || ""} onChange={(e) => update("gtin", e.target.value)} className="input-vintage" /></Field>
            <Field label="MPN (optional)"><input value={p.mpn || ""} onChange={(e) => update("mpn", e.target.value)} className="input-vintage" /></Field>
          </div>
          <p className="text-[11px] text-parchment/50 mt-2">Do not fabricate — leave blank if unknown.</p>
        </details>

        <div className="flex gap-3 pt-6 border-t border-brass-soft">
          <button onClick={save} disabled={saving} data-testid="pe-save" className="btn-gold">{saving ? "Saving…" : "Save Product"}</button>
          <button onClick={() => nav("/admin/products")} className="btn-outline">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (<div><label className="font-caption text-[10px] text-brass block mb-1">{label}</label>{children}</div>);
}
